import { useCallback, useEffect, useState } from 'react';

export interface DefinitionResource<T> {
  readonly data?: T;
  readonly error?: Error;
  readonly loading: boolean;
  readonly reload: () => void;
}
interface Answer<T> {
  readonly data?: T;
  readonly error?: Error;
  readonly loading: boolean;
  /** The fetcher this answer belongs to, so a previous fetcher's answer is never returned for the current one. */
  readonly fetcher?: (signal: AbortSignal) => Promise<T>;
}
/** `keepPrevious` leaves the last answer on screen while the next one loads, for reloads of the same list. */
export const useDefinitionResource = <T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  { keepPrevious = false }: { readonly keepPrevious?: boolean } = {}
): DefinitionResource<T> => {
  const [state, setState] = useState<Answer<T>>({ loading: true });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setState((prev) => ({ data: keepPrevious ? prev.data : undefined, loading: true, fetcher }));
    fetcher(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ data, loading: false, fetcher });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setState((prev) => ({
            data: keepPrevious ? prev.data : undefined,
            loading: false,
            error: error instanceof Error ? error : new Error(String(error)),
            fetcher
          }));
      });
    return () => controller.abort();
  }, [fetcher, revision, keepPrevious]);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  // Loading on the render a new fetcher arrives: the effect alone left one render showing the previous answer.
  if (state.fetcher !== fetcher) return { data: keepPrevious ? state.data : undefined, loading: true, reload };
  return { data: state.data, error: state.error, loading: state.loading, reload };
};
